import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Vertical org-style tree for entity relationships (parent → children →
 * grandchildren). Pure presentation: callers hand in a root node whose
 * children nest arbitrarily deep; each node renders as a small card hung off
 * a connector rail so stacked relationships read at a glance.
 *
 * - `badges` is a free slot (chips, counts) rendered beside the name.
 * - `meta` is the quiet second line (account, geography, id …).
 * - `muted` greys a node (e.g. an inactive company) without hiding it.
 * - `onNodeClick` + `selectedId` make nodes navigable when the host wants
 *   that; leave them off for a static diagram.
 */

export interface RelationshipTreeNode {
  id: string;
  name: string;
  meta?: React.ReactNode;
  badges?: React.ReactNode;
  muted?: boolean;
  children?: RelationshipTreeNode[];
}

export interface RelationshipTreeProps {
  root: RelationshipTreeNode;
  onNodeClick?: (node: RelationshipTreeNode) => void;
  selectedId?: string | null;
  className?: string;
}

export function RelationshipTree({
  root,
  onNodeClick,
  selectedId,
  className,
}: RelationshipTreeProps) {
  return (
    <div className={cn("overflow-x-auto", className)}>
      <TreeNodeRow
        node={root}
        depth={0}
        isLast
        onNodeClick={onNodeClick}
        selectedId={selectedId ?? null}
      />
    </div>
  );
}

function TreeNodeRow({
  node,
  depth,
  isLast,
  onNodeClick,
  selectedId,
}: {
  node: RelationshipTreeNode;
  depth: number;
  isLast: boolean;
  onNodeClick?: (node: RelationshipTreeNode) => void;
  selectedId: string | null;
}) {
  const children = node.children ?? [];
  const selected = selectedId === node.id;
  const clickable = !!onNodeClick;

  const card = (
    <div
      className={cn(
        // max-w so a very long name/meta truncates instead of stretching the
        // card (and the whole tree's horizontal scroll) indefinitely.
        "inline-flex min-w-0 max-w-md items-center gap-2 rounded-md border px-3 py-1.5 text-sm",
        depth === 0
          ? "border-brand/40 bg-brand/5"
          : "border-border bg-background",
        selected && "ring-2 ring-brand",
        node.muted && "opacity-60",
        clickable && "cursor-pointer transition-colors hover:border-brand/50",
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "truncate font-medium",
              depth === 0 && "text-brand",
            )}
          >
            {node.name}
          </span>
          {node.badges}
        </div>
        {node.meta ? (
          <div className="truncate text-xs text-muted-foreground">{node.meta}</div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={cn("relative", depth > 0 && "pl-6")}>
      {/* Connector: vertical rail from the sibling above, horizontal stub
          into this card. The stub (and, for the last sibling, the rail's end)
          anchor to the CARD wrapper's midline, so alignment holds whatever
          the card's height (one line, or name + meta). */}
      {depth > 0 && !isLast && (
        <span aria-hidden className="absolute left-2 top-0 h-full w-px bg-border" />
      )}
      <div className="relative py-1">
        {depth > 0 && (
          <>
            {isLast && (
              <span aria-hidden className="absolute -left-4 top-0 h-1/2 w-px bg-border" />
            )}
            <span aria-hidden className="absolute -left-4 top-1/2 h-px w-4 bg-border" />
          </>
        )}
        {clickable ? (
          <button
            type="button"
            onClick={() => onNodeClick?.(node)}
            className="rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {card}
          </button>
        ) : (
          card
        )}
      </div>
      {children.length > 0 && (
        <div>
          {children.map((child, i) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              isLast={i === children.length - 1}
              onNodeClick={onNodeClick}
              selectedId={selectedId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
